import { getInvoiceScheduleBlockMessage } from "@common/helpers/domains/accounting/invoiceScheduleEligibility";
import { MatchedSchedule } from "@common/schemas/domains/accounting/scheduleMatchedDisplay.schema";
import { InvoiceLineItemSchedule } from "@common/schemas/domains/invoice/invoice-response.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { Invoice } from "@common/types/invoice";
import { SchedulableInvoiceLine } from "@common/helpers/domains/accounting/invoiceScheduleLines";
import { getInvoiceScheduleAmount } from "../scheduleApplyInvoiceHelpers";
import { BulkScheduleRow } from "./bulkScheduleRows";

export interface BulkScheduleInvoice extends Pick<
    Invoice,
    | "id"
    | "entityId"
    | "total"
    | "invoiceDate"
    | "status"
    | "currency"
    | "schedule"
    | "matchedSchedules"
> {
    lines?: Array<
        SchedulableInvoiceLine & {
            schedule?: InvoiceLineItemSchedule;
            matchedSchedules?: MatchedSchedule[];
        }
    >;
}

export function toInvoiceBulkScheduleRow(
    invoice: BulkScheduleInvoice,
): BulkScheduleRow {
    const lines = invoice.lines ?? [];
    const wholeInvoiceApplicationId =
        invoice.schedule?.scope === "invoice"
            ? invoice.schedule.scheduleApplicationId
            : null;
    // One line-scoped application can cover several lines.
    const existingApplicationIds =
        wholeInvoiceApplicationId !== null
            ? [wholeInvoiceApplicationId]
            : [
                  ...new Set(
                      lines.flatMap((line) =>
                          line.schedule
                              ? [line.schedule.scheduleApplicationId]
                              : [],
                      ),
                  ),
              ];
    // The server fills invoice-level matches only for an unscheduled invoice;
    // a partially scheduled one carries them per line.
    const hasMatchedSchedule =
        (invoice.matchedSchedules?.length ?? 0) > 0 ||
        lines.some((line) => (line.matchedSchedules?.length ?? 0) > 0);
    return {
        entityId: invoice.entityId,
        source: {
            sourceType: ScheduleSourceType.invoice,
            invoiceId: invoice.id,
            amount: getInvoiceScheduleAmount({
                lines: invoice.lines,
                fallbackAmount: invoice.total,
            }),
        },
        existingApplicationIds,
        existingTemplateId:
            wholeInvoiceApplicationId !== null
                ? (invoice.schedule?.scheduleTemplateId ?? null)
                : null,
        hasMatchedSchedule,
        blockReason: getInvoiceScheduleBlockMessage(invoice),
        transactionAmount: null,
    };
}
